import * as React from "react";
import { cn } from "../../lib/utils";
import { ResponsiveGrid, type ResponsiveGridColumns } from "./ResponsiveGrid";

/**
 * StatsBar / StatCard — the counter strip that sits under a list page's
 * header and reports how the rows below break down.
 *
 * This markup had been hand-copied onto every list page that wanted counts,
 * so it lives here once. It is purely presentational: it takes already-loaded
 * numbers as props and holds no query, no router access and no session.
 *
 * A card is a plain readout unless it is given an `onClick`, in which case it
 * renders as a button and can show an `active` state. That variant was the
 * shape this component was designed for; adding it changed no existing caller,
 * because a card without `onClick` renders exactly the DOM it always did.
 */

/**
 * Cards per row. Identical to `ResponsiveGridColumns` by construction rather
 * than by a comment asking two lists to be kept in step — the grid is what
 * actually lays the bar out, so it is what defines the legal counts.
 */
export type StatsBarColumns = ResponsiveGridColumns;

/**
 * 🔴 THE NARROWEST A STAT TILE MAY GET BEFORE THE BAR SHEDS A COLUMN, and the
 * one number that had to be derived rather than guessed. `ResponsiveGrid`
 * defaults to 20rem, which is right for a record body and catastrophic here: a
 * six-tile strip at 20rem needs 6·320 + 5·16 = 2000px of container, so it would
 * have collapsed to two columns at every width the app is ever rendered at — a
 * summary strip silently becoming a 3×2 block, with nothing in the console.
 *
 * The container is `AppShell`'s `<main>`: `max-w-content` (1200px, border-box)
 * less `md:p-space-5` either side, so 1200 - 2·32 = **1136px** at full page
 * width. The bar's gap is 16px. A count N survives while
 * `N·min + (N-1)·16 ≤ container`, so the ceiling on `min` is
 * `(container - (N-1)·16) / N`. Two ceilings bind, from the two behaviours this
 * conversion had to preserve exactly:
 *
 *   six across at full page width  → (1136 - 5·16)/6 = 176px
 *   two across on a 375px phone    → ((375 - 2·24) - 16)/2 = 155px
 *
 * 9.5rem = 152px is the largest round value under both, so the bar still holds
 * six at 1136px (144px of slack — a scrollbar or a shell with more padding
 * cannot silently shed a column) and still holds two on a phone. Between those
 * ends it now interpolates instead of jumping at `md`, which is the point:
 * `columns={6}` at a 768px viewport used to paint six ~104px tiles, and now
 * paints four ~164px ones.
 *
 * ONE FLOOR, NOT A TABLE PER COUNT — deliberately. `minColWidth` decides only
 * WHEN a column is dropped; the surviving tracks are `1fr` and stretch to fill
 * regardless. So a larger minimum at a low count would not widen anything, it
 * would only make that bar collapse sooner. A per-count table would also bake
 * the 1136px page width into a component whose entire purpose is not to know
 * its container's width.
 *
 * Where each count sheds its first column, at `N·152 + (N-1)·16`:
 * 6 → 992px · 5 → 824px · 4 → 656px · 3 → 488px · 2 → 320px.
 *
 * 🔴 THE 1136px CEILING THIS WAS DERIVED AGAINST IS NOW 1536px (2026-08-22),
 * AND NOTHING HERE CHANGES. `AppShell`'s `max-w-content` went 1200px → 1600px,
 * so at a 1900px window the bar's container is 1536px rather than 1136px. Both
 * ceilings above still bind the same way — six across now needs `min ≤
 * (1536-80)/6 = 242.67px`, which 9.5rem clears by more than before, and the
 * 375px phone ceiling is untouched because the shell's cap never bound there.
 * Recorded rather than recomputed, because a derivation is only as good as the
 * number it was derived against and the next reader would otherwise re-derive
 * 176px from a ceiling that has moved.
 *
 * WHAT IT FIXED FOR FREE. `FURTHEST SUBMISSION` (168.97px) and `DAYS SINCE
 * ASSIGNMENT` (182.52px) wrapped to two lines against a 142px label box, and no
 * column rule could have fixed that at 1136px: six across capped the box at
 * 144px and five across missed by 0.12px. A tile gives `tileW - 34` (padding
 * 2×16 plus a 1px border either side), so a 182.52px label needs a 216.52px
 * tile, i.e. a 1379px container. Measured after the cap moved: a 1900px window
 * gives 242.67px tiles and a 208.67px label box, and neither label wraps. The
 * clearing point is a ~1700px window with the sidebar expanded, ~1508px with it
 * collapsed — below that they still wrap, which is the honest limit.
 */
const STAT_TILE_MIN_WIDTH = "9.5rem";

export interface StatsBarProps {
  /**
   * Lay the cards out as an even grid this many across. Omit for a wrapping
   * flow instead — the layout for a bar that mixes bare cards with captioned
   * StatGroups, where the groups must not be forced into equal columns.
   */
  columns?: StatsBarColumns;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
}

export function StatsBar({
  columns,
  className,
  children,
  "data-testid": testId,
}: StatsBarProps) {
  // The wrapping flow is NOT a grid and must not become one: its whole job is
  // to let captioned StatGroups size to their own contents rather than be
  // forced into equal columns. It is left exactly as it was.
  if (!columns) {
    return (
      <div
        className={cn("flex flex-wrap items-end gap-space-4 mb-space-4", className)}
        data-testid={testId}
      >
        {children}
      </div>
    );
  }

  return (
    <ResponsiveGrid
      cols={columns}
      minColWidth={STAT_TILE_MIN_WIDTH}
      // `sm` is 16px — the same gap `gap-space-3` gave, so the bar's rhythm is
      // unchanged. It is passed rather than set in `className` because the
      // template's arithmetic reads the gap back out of `--phx-gap`; a gap
      // applied any other way would be absent from the column maths.
      gap="sm"
      // `items-stretch` is load-bearing and has to be stated. `ResponsiveGrid`
      // sets `items-start` — right for a record body, where a short card should
      // not be padded out to match a tall neighbour — but a counter strip reads
      // as one strip only if the tiles share an edge, and a two-line label on
      // one tile would otherwise leave it standing proud of the row. `cn` is
      // tailwind-merge, so this later value wins the `align-items` group
      // outright; concatenation would have left both and lost the argument.
      className={cn("items-stretch mb-space-4", className)}
      data-testid={testId}
    >
      {children}
    </ResponsiveGrid>
  );
}

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
  "data-testid"?: string;
  /**
   * Makes the card a filter control. Supplying it renders a `<button>` instead
   * of a `<div>`; omitting it leaves the card exactly as it was.
   */
  onClick?: () => void;
  /** Whether this card's filter is the one currently applied. Ignored without
   *  `onClick` — a readout has nothing to be active about. */
  active?: boolean;
}

/**
 * One captioned counter, optionally a filter control.
 *
 * A clickable card is a real `<button>` rather than a div with a handler, so it
 * is keyboard-reachable and announced as a control; `aria-pressed` carries the
 * active state, because the visual ring alone says nothing to a screen reader.
 */
export function StatCard({
  label,
  value,
  className,
  "data-testid": testId,
  onClick,
  active,
}: StatCardProps) {
  const body = (
    <>
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="min-w-0 font-display text-h3 text-onyx">{value}</div>
    </>
  );

  // `min-w-0` is the load-bearing part, and it is about the CARD's own box, not
  // its contents. A card is always a grid item (`columns`) or a flex item (the
  // wrapping flow, and StatGroup's inner row), and both default to
  // `min-width: auto` — i.e. never narrower than their content. `grid-cols-6`
  // lays down `minmax(0, 1fr)` tracks, so at six across the track is genuinely
  // narrow, and a value slot holding a `StatusBadge` (which is
  // `whitespace-nowrap`) made the card refuse its track and spill over the
  // neighbouring card. With `min-w-0` the card takes its track and the grid
  // stays intact.
  //
  // HONEST LIMIT: this stops the card overflowing the GRID; it cannot make a
  // `nowrap` child wrap, so a badge longer than a very narrow card still paints
  // past the card's padding. Fixing that needs either `truncate` (clips, and
  // would silently cut every numeric and textual value this slot is given) or
  // turning the value slot into a wrapping flex row (a restyle of every
  // multi-element `value` in the app). Neither belongs in a shrink fix.
  //
  // A `[&_[data-tone]]:text-caption` descendant rule used to live on this base.
  // It existed because `cn` stripped `StatusBadge`'s own `text-caption`, so a
  // badge used as a tile's value inherited the value slot's `text-h3` and
  // rendered at 28px. `cn` now knows this project's font-size scale
  // (`design-system/lib/utils.ts`), so the badge carries its own size and the
  // rule is not merely redundant — it was a SPECIFICITY TRAP. At (0,2,0) it
  // beat the badge's own (0,1,0), so it silently overrode any size a caller
  // deliberately chose for a toned element in this slot. Removed with the
  // repair, deliberately and in the same change.
  const base =
    "border border-border-light rounded-md p-space-3 bg-white-pure min-w-0";

  if (!onClick) {
    return (
      <div className={cn(base, className)} data-testid={testId}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ?? false}
      className={cn(
        base,
        "text-left transition-colors hover:border-gold focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-gold",
        active && "border-gold bg-white-warm",
        className,
      )}
      data-testid={testId}
    >
      {body}
    </button>
  );
}

export interface StatGroupProps {
  label: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
}

/**
 * A captioned run of cards, ruled off from its neighbour. Use when one bar
 * carries two different axes over the same rows — without the caption and the
 * rule they read as a single series that double-counts.
 */
export function StatGroup({
  label,
  className,
  children,
  "data-testid": testId,
}: StatGroupProps) {
  return (
    <div
      className={cn(
        "pl-0 border-l-0 lg:pl-space-4 lg:border-l lg:border-border-light",
        className,
      )}
      data-testid={testId}
    >
      <div className="text-label uppercase tracking-wide text-gray-mid mb-space-1">
        {label}
      </div>
      <div className="flex flex-wrap gap-space-3">{children}</div>
    </div>
  );
}
